import { LEAGUES, type LeagueId } from './balance.ts';

/**
 * Cosmetics (GDD §15): only what playing unlocks, never anything bought. Each is a value of one
 * part of the silhouette (`shared/account/avatar.ts`) or a theme of the blueprint, with the rule that
 * opens it. What is not listed here is free for everybody. Names are in the translation files.
 */

export type CosmeticPart = 'hat' | 'mask' | 'coat' | 'rim' | 'suit' | 'blueprint' | 'emote' | 'car';

export type Unlock =
  | { kind: 'level'; level: number }
  | { kind: 'trophy'; trophy: string }
  | { kind: 'league'; league: LeagueId };

export interface CosmeticDef {
  /** `part:value`. */
  id: string;
  part: CosmeticPart;
  value: string;
  unlock: Unlock;
}

const item = (part: CosmeticPart, value: string, unlock: Unlock): CosmeticDef => ({
  id: `${part}:${value}`,
  part,
  value,
  unlock,
});

export const COSMETICS: readonly CosmeticDef[] = [
  item('coat', 'crimson', { kind: 'level', level: 2 }),
  item('hat', 'tophat', { kind: 'level', level: 3 }),
  item('mask', 'fox', { kind: 'level', level: 5 }),
  item('blueprint', 'sepia', { kind: 'level', level: 6 }),
  item('hat', 'panama', { kind: 'level', level: 8 }),
  item('blueprint', 'noir', { kind: 'level', level: 10 }),
  item('coat', 'ivory', { kind: 'level', level: 12 }),
  item('rim', 'ghost', { kind: 'level', level: 15 }),
  item('hat', 'hood', { kind: 'trophy', trophy: 'clean_getaway' }),
  item('mask', 'skull', { kind: 'trophy', trophy: 'mole_hunter' }),
  item('rim', 'gold', { kind: 'trophy', trophy: 'fifty_heists' }),
  item('suit', 'crown', { kind: 'league', league: 'master' }),
  // Emotes (GDD §13.3; the first four are free) and the getaway car of the escape puzzle (the sedan is free).
  item('emote', 'cigar', { kind: 'level', level: 4 }),
  item('car', 'coupe', { kind: 'level', level: 7 }),
  item('emote', 'watch', { kind: 'level', level: 9 }),
  item('car', 'taxi', { kind: 'level', level: 11 }),
  item('emote', 'glass', { kind: 'level', level: 13 }),
  item('emote', 'magnifier', { kind: 'trophy', trophy: 'mole_hunter' }),
  item('car', 'roadster', { kind: 'trophy', trophy: 'clean_getaway' }),
];

/** Whether a player may send an emote: the free ones, and the ones their cosmetics include. */
export function emoteOwned(id: string, owned: ReadonlySet<string>): boolean {
  return !COSMETICS.some((c) => c.id === `emote:${id}`) || owned.has(`emote:${id}`);
}

/** The key of a cosmetic in the translation files: the id with its colon made an underscore (a colon separates namespaces in i18next). */
export const cosmeticWordKey = (id: string): string => id.replace(':', '_');

export const COSMETIC_IDS: readonly string[] = COSMETICS.map((c) => c.id);

export function cosmeticById(id: string): CosmeticDef | undefined {
  return COSMETICS.find((c) => c.id === id);
}

/** What a player has reached, to ask which cosmetics it opens. */
export interface Reached {
  level: number;
  trophies: ReadonlySet<string>;
  /** The best league reached, by its position in `LEAGUES` (−1: none shown yet). */
  leagueRank: number;
}

/** Whether a rule is met. */
export function unlocked(def: CosmeticDef, reached: Reached): boolean {
  const rule = def.unlock;
  switch (rule.kind) {
    case 'level':
      return reached.level >= rule.level;
    case 'trophy':
      return reached.trophies.has(rule.trophy);
    case 'league':
      return reached.leagueRank >= LEAGUES.findIndex((l) => l.id === rule.league);
  }
}

/** The cosmetics that `reached` opens and the player does not have yet. */
export function cosmeticsOpened(reached: Reached, have: ReadonlySet<string>): string[] {
  return COSMETICS.filter((c) => !have.has(c.id) && unlocked(c, reached)).map((c) => c.id);
}
